import { Box, Flex, chakra } from "@chakra-ui/react";
import { Instant } from "@js-joda/core";
import React from "react";
import { Messages } from "../../core/api";
import { dateFormatter } from "../../shared/utils/date-formatter";
import { PRINT_FONT_FAMILY } from "./question-types/printStyles";

// The letter-size "paper" both review layouts sit on, mimicking the generated PDF's page chrome
// (medflyt-serverless renderDocument.ts): agency header, centred title, grey patient-details box.

export type DocumentReviewHeader = Messages["AgencyDocumentReviewHeader"];

/** US letter at 96dpi. */
const PAPER_WIDTH = "816px";

const KeyValue = chakra("div", {
  baseStyle: {
    whiteSpace: "nowrap",
  },
});

function InlineKeyValue(props: { label: string; value: string }) {
  return (
    <KeyValue>
      <chakra.span fontWeight={600}>{props.label}:</chakra.span> {props.value}
    </KeyValue>
  );
}

interface Props {
  children: React.ReactNode;
  header: DocumentReviewHeader;
  submittedAt: Instant | null;
  title: string;
}

export default function DocumentPaper(props: Props) {
  return (
    <Box
      bg="white"
      boxShadow="0 1px 3px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.06)"
      color="black"
      fontFamily={PRINT_FONT_FAMILY}
      fontSize="12px"
      maxW="100%"
      mx="auto"
      pb="56px"
      pt="32px"
      px="48px"
      w={PAPER_WIDTH}
    >
      <Flex
        align="flex-start"
        borderBottom="1px solid #eaeaea"
        direction="column"
        gap="2px"
        pb="8px"
      >
        <chakra.div fontSize="12px" fontWeight={600}>
          {props.header.agencyName}
        </chakra.div>
        {props.header.agencyAddress !== null && (
          <chakra.div fontSize="10px" fontWeight={500}>
            {props.header.agencyAddress}
          </chakra.div>
        )}
      </Flex>

      <chakra.h1
        fontSize="16px"
        fontWeight={600}
        lineHeight="22px"
        m={0}
        pb="16px"
        pt="24px"
        textAlign="center"
      >
        {props.title}
      </chakra.h1>

      <Flex
        bg="#f9f9f9"
        borderRadius="8px"
        columnGap="32px"
        mb="24px"
        px="12px"
        py="14px"
        rowGap="10px"
        wrap="wrap"
      >
        <InlineKeyValue label="Patient Name" value={props.header.patientName} />
        <InlineKeyValue label="Clinician Name" value={props.header.caregiverName ?? "—"} />
        <InlineKeyValue
          label="Submitted"
          value={
            props.submittedAt !== null ? dateFormatter.toDate(props.submittedAt) : "Not submitted"
          }
        />
      </Flex>

      {props.children}
    </Box>
  );
}
